import React from 'react';

import { Link } from 'react-router-dom';

import ClosedBadge from '../common/ClosedBadge';
import CourseLogo from '../common/CourseLogo';

interface CourseCellProps {
  logoFileId?: string;
  name: string;
  address?: string;
  // Where the course name links to, e.g. the course's public profile page. Plain text when absent.
  href?: string;
  // A closed course shows the shared Closed logo (its own is not even fetched) and when it closed.
  closed?: boolean;
  closedYear?: number;
  closedMonth?: number;
}

// Shared "course" cell for member-facing tables (Home Courses / Courses Played), resolves the
// course's logo lazily per row, same lazy-per-item pattern used on the public Explore map, so a
// member's own small list never triggers more than a handful of SAS lookups at once. A closed
// course shows the shared Closed logo instead and skips the lookup (see utils/courseClosure.ts).
const CourseCell: React.FC<CourseCellProps> = ({
  logoFileId,
  name,
  address,
  href,
  closed = false,
  closedYear,
  closedMonth,
}) => (
  <div className="flex items-center gap-3">
    {/* Not on a phone: in a member's table the course name needs that width more than the logo
          does, and the logo is still one tap away on the course's own page. */}
    <CourseLogo
      logoFileId={logoFileId}
      name={name}
      closed={closed}
      className="hidden h-10 w-10 rounded sm:flex"
    />
    <div className="min-w-0">
      {href ? (
        <Link
          to={href}
          className="block truncate font-medium text-gray-700 underline-offset-2 hover:text-primary-deep hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:text-gray-300 dark:hover:text-primary"
        >
          {name}
        </Link>
      ) : (
        <p className="truncate font-medium text-gray-700 dark:text-gray-300">
          {name}
        </p>
      )}
      {address && (
        <p className="truncate text-xs text-gray-500 dark:text-gray-400">
          {address}
        </p>
      )}
      {closed && (
        <ClosedBadge
          tone="light"
          closed={closed}
          closedYear={closedYear}
          closedMonth={closedMonth}
          className="mt-1"
        />
      )}
    </div>
  </div>
);

export default CourseCell;
